/* eslint-disable react-hooks/exhaustive-deps */
import { Box, useTheme, Button, Typography, Modal, IconButton, TextField, Grid } from "@mui/material";
import "@styles/stylesRegistro.css";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { ApiError } from "@interfaces/response";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomContryCode from "./CustomContryCode/CustomContryCode";
import { PHONE_NUMBER } from "@utils/expreciones";
import { useFormik } from "formik";
import * as yup from "yup";
import { setAccountUserPhone } from "@store/slices/registerSlice";
import { usePatchUserMutation, useRequestPhoneCodeMutation } from "@api/onboarding";

interface ModalPhoneProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ModalBuroNip = (props: ModalPhoneProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [patchUser] = usePatchUserMutation();
  const [requestPhoneCode] = useRequestPhoneCodeMutation();

  const formik = useFormik({
    initialValues: {
      country_code: accountUser.code_phone || "+52",
      phone: accountUser.phone || "",
    },
    validationSchema: yup.object().shape({
      country_code: yup.string().required("Campo requerido"),
      phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!formik.isValid) {
          return;
        }

        // validar si el número de teléfono es el mismo
        if (accountUser.phone === values.phone && accountUser.code_phone === values.country_code) {
          console.log("no hay cambios");
          props.onClose();
          return;
        }

        // actualizar el número de teléfono
        await patchUser({
          id: String(accountUser.id),
          data: {
            phone: `${values.country_code}${values.phone}`,
          },
        }).unwrap();

        // solicitar el código de verificación
        await requestPhoneCode({
          country_code: values.country_code,
          phone_number: values.phone,
        }).unwrap();

        dispatch(
          setAccountUserPhone({
            id: accountUser.id,
            phone_full: `${values.country_code}${values.phone}`,
            code_phone: values.country_code,
            phone: values.phone,
          }),
        );

        // cerrar el modal
        props.onSuccess();
      } catch (error) {
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal open={props.open}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          textAlign: "center",
          width: "70%",
          maxWidth: "500px",
          outline: "none",
        }}
      >
        <IconButton
          aria-label="close"
          onClick={props.onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography fontWeight={"600"} gutterBottom color={theme?.palette?.primary?.dark}>
          Actualizar número de teléfono
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={1} mt={2}>
            <Grid item xs={4} sm={4} md={4}>
              <CustomContryCode
                label="Código"
                name="country_code"
                value={formik.values.country_code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                disabled={formik.isSubmitting}
                required={true}
              />
            </Grid>

            <Grid item xs={8} sm={8} md={8}>
              <TextField
                type="text"
                name="phone"
                variant="outlined"
                label="Teléfono"
                fullWidth
                value={formik.values.phone}
                required
                onChange={(event) => {
                  formik.setFieldValue("phone", event.target.value.replace(/[^0-9]/g, ""));
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric",
                }}
                disabled={formik.isSubmitting}
              />
            </Grid>
          </Grid>
          <Button type="submit" variant="default" color="primary" sx={{ mt: 2 }}>
            Confirmar
          </Button>
        </form>
      </Box>
    </Modal>
  );
};

export default ModalBuroNip;
